import React, { memo, useMemo } from 'react';
import { Icon, Text, Link, useTheme } from '@Sage/hoi-poi-ui';
import ContentLoader from 'react-content-loader';
import { getLiteral, getLiteralWithParametersHtml } from 'utils/getLiteral';
import { getRoute } from 'utils/routes';
import { logEvent } from 'utils/tracking';
import { getBackendBoolean } from '@Sage/ssm-utils/backendScalars';
import Row from './Row';

import './styles.scss';

/**
 * Presentational list for featured fields: header + scrollable body (loader, rows, or empty state).
 * No button, no modal. Used by CompanyDetailWidget and OpportunityDetailWidget (with button in footer).
 */
const FeaturedFieldsList = memo(
    ({
        allExtraFields = [],
        bodyRef,
        canConfigImplementation,
        entity,
        finalSections = [],
        isLoading,
        productModeCode,
    }) => {
        const theme = useTheme();

        const header = useMemo(
            () => (
                <div className="featured-fields__header">
                    <Text type="subtitle" medium>
                        {getLiteral('label_featured_customfields')}
                    </Text>
                </div>
            ),
            [],
        );

        const emptyContent = useMemo(() => {
            let emptyComp = (
                <Text
                    color={theme.colors.utility.textDisabled}
                    className="featured-fields__body__title"
                >
                    {getLiteral('label_no_featured_customfields_feature_fields')}
                </Text>
            );

            if (!allExtraFields.length) {
                emptyComp =
                    canConfigImplementation && productModeCode !== 'business' ? (
                        <Text
                            color={theme.colors.utility.textDisabled}
                            className="featured-fields__body__title"
                        >
                            {getLiteralWithParametersHtml(
                                'label_no_featured_customfields_create_field',
                                [
                                    [
                                        <span key="create-field-link">
                                            <Link
                                                href={getRoute(
                                                    `/settings/fields?showCrud=true&tab=${entity?.extraFieldTab}`,
                                                )}
                                                className="featured-fields__body__link"
                                                onClick={() =>
                                                    logEvent({
                                                        event: entity?.trueName,
                                                        submodule: 'infoWidget',
                                                        functionality: 'createField',
                                                    })
                                                }
                                                bold
                                            >
                                                {getLiteral(
                                                    'label_no_featured_customfields_create_field_link',
                                                )}
                                            </Link>
                                        </span>,
                                    ],
                                ],
                            )}
                        </Text>
                    ) : (
                        <Text
                            color={theme.colors.utility.textDisabled}
                            className="featured-fields__body__title"
                        >
                            {getLiteral('label_no_featured_customfields')}
                        </Text>
                    );
            }

            return (
                <div className="featured-fields__body--empty">
                    <div>
                        <Icon
                            name="starBorder"
                            size="big"
                            color={theme.colors.utility.textDisabled}
                        />
                    </div>
                    {emptyComp}
                </div>
            );
        }, [
            allExtraFields.length,
            canConfigImplementation,
            entity?.extraFieldTab,
            entity?.trueName,
            productModeCode,
            theme,
        ]);

        const content = useMemo(() => {
            const rows = [];
            finalSections.forEach((section) => rows.push(...section.data));
            const featuredRows = rows.filter((field) => getBackendBoolean(field.isRelevant));
            if (!featuredRows.length || !allExtraFields.length) return emptyContent;

            return featuredRows.map((field, index) => (
                <Row key={field.id ?? index} description={field.description} value={field.value} />
            ));
        }, [allExtraFields.length, emptyContent, finalSections]);

        const loader = useMemo(
            () => (
                <ContentLoader
                    height={180}
                    width={400}
                    speed={2}
                    backgroundColor={theme.colors.grey[50]}
                    foregroundColor={theme.colors.grey[100]}
                >
                    <rect x="0" y="20" rx="3" ry="3" width={80} height="8" />
                    <rect x="120" y="20" rx="3" ry="3" width={200} height="8" />
                    <rect x="0" y="50" rx="3" ry="3" width={80} height="8" />
                    <rect x="120" y="50" rx="3" ry="3" width={200} height="8" />
                    <rect x="0" y="80" rx="3" ry="3" width={80} height="8" />
                    <rect x="120" y="80" rx="3" ry="3" width={200} height="8" />
                    <rect x="0" y="110" rx="3" ry="3" width={80} height="8" />
                    <rect x="120" y="110" rx="3" ry="3" width={200} height="8" />
                    <rect x="0" y="140" rx="3" ry="3" width={80} height="8" />
                    <rect x="120" y="140" rx="3" ry="3" width={200} height="8" />
                    <rect x="0" y="170" rx="3" ry="3" width={80} height="8" />
                    <rect x="120" y="170" rx="3" ry="3" width={200} height="8" />
                </ContentLoader>
            ),
            [theme.colors.grey],
        );

        return (
            <div className="featured-fields__container">
                {header}
                <div ref={bodyRef} className="featured-fields__body">
                    {isLoading && loader}
                    {!isLoading && content}
                </div>
            </div>
        );
    },
);

FeaturedFieldsList.displayName = 'FeaturedFieldsList';

export default FeaturedFieldsList;
